'use client';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import { PrismicRichText } from '@prismicio/react';
import { asHTML, Content } from '@prismicio/client';
import { ImQuotesLeft } from 'react-icons/im';

type ReviewsSliderDocument = Content.ReviewsSliderDocument;

export interface ReviewsSliderProps {
  data: ReviewsSliderDocument['data'][];
}

const vignetteClass =
  "after:content-[''] after:absolute after:inset-0 after:z-1 after:pointer-events-none sm:border-b sm:border-[#222] after:bg-gradient-to-t after:from-blue-800 after:to-blue-800 after:opacity-80";

export default function ReviewsSlider({ data }: ReviewsSliderProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const reviews = data.flatMap((item) => item.review || []);

  useEffect(() => {
    if (reviews.length === 0) return;

    const timer = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide + 1) % reviews.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [reviews]);

  if (reviews.length === 0) {
    return <div className="p-8 text-center">No reviews available.</div>;
  }

  return (
    <div className="relative h-full w-full overflow-hidden">
      <AnimatePresence>
        {reviews.map((review, index) => (
          <motion.div
            key={index}
            className={`absolute inset-0 flex items-end p-4 pb-6 sm:p-8 ${
              index === currentSlide ? 'z-10' : 'z-0'
            }`}
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentSlide ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          >
            <div className={`absolute inset-0 ${vignetteClass}`}>
              {review.image.url && (
                <Image
                  src={review.image.url || ''}
                  alt={review.image.alt || ''}
                  fill
                  sizes="100vw"
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                  className="grayscale filter"
                  priority={index === 0}
                />
              )}
            </div>
            <div className="relative z-10 mb-2 max-w-2xl text-white sm:mb-16 sm:pl-4">
              <ImQuotesLeft className="relative top-8 text-[4.5em] opacity-20" />
              <blockquote className="mb-6 text-2xl font-normal leading-tight opacity-90 sm:opacity-100 lg:text-3xl">
                <PrismicRichText field={review.review} />
              </blockquote>
              <div
                dangerouslySetInnerHTML={{ __html: asHTML(review.name) }}
                className="text-xl"
              ></div>
              <p className="text-base italic opacity-50">{review.position}</p>
              <p className="text-base italic opacity-50">{review.company}</p>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
